import React, { useEffect, useState } from 'react';
import { ACCENT, ACCENT_LIGHT, ACCENT_BORDER, TEXT_DARK, TEXT_MID, TEXT_LIGHT, FONT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, ScreenWrap, SectionLabel } from '../components/ui/Primitives';
import { openPlace, openDirections } from '../lib/maps';
import { useBreakpoint } from '../lib/useBreakpoint';

const CAMPUS_MAP_URL = '/resources/scuhs-campus-map.png';

// TODO before launch: confirm venue address.
const VENUE = {
  name: 'Southern California University of Health Sciences',
  address: '16200 E Amber Valley Dr, Whittier, CA 90604',
};

// Hotel query string — Apple Maps resolves the place from the name itself.
const HOTEL_NAME = 'La Quinta Inn & Suites by Wyndham La Habra';
const HOTEL_QUERY = 'La Quinta Inn & Suites by Wyndham La Habra, CA';

// Group dinner reservation, Thursday May 21. About 15 min from SCUHS.
const DINNER = {
  name: 'Yard House',
  address: '160 S Brea Blvd, Brea, CA 92821',
  when: 'Thursday · 5:30 PM',
};

// Wi-Fi: structure kept, values intentionally blank until populated.
const WIFI = [
  { key: 'ssid', label: 'Network', value: '' },
  { key: 'pwd',  label: 'Password', value: '' },
];

const CONTACTS = [
  { role: 'Executive Director, ChiroCongress',     name: 'Lizz Klein', phone: '+15033417833' },
  { role: 'ChiroCongress Executive Director At-Large', name: 'Marc Abla', phone: '+12173413023' },
];

export default function LogisticsScreen() {
  const [copied, setCopied] = useState(null);
  const [showDirections, setShowDirections] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const copy = (key, val) => {
    if (!val) return;
    if (navigator.clipboard) navigator.clipboard.writeText(val).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  // IMPORTANT: define wrappers at module scope (below) — declaring them inside
  // the render function would make them new component types on every render
  // and reset scroll position whenever this component re-renders.
  const OuterWrap = isDesktop ? DesktopLogisticsWrap : ScreenWrap;
  const ColumnWrap = isDesktop ? DesktopColumn : PassThrough;

  return (
    <OuterWrap>
      <ColumnWrap>
      <SectionLabel>VENUE</SectionLabel>
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>
          {VENUE.name}
        </div>
        <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5, marginBottom: 12 }}>
          {VENUE.address.split(',')[0]}
          <br />
          {VENUE.address.split(',').slice(1).join(',').trim()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => openPlace(VENUE.address)} style={mapBtn}>
            <Ic.Map size={15} color="#fff" /> Open in Maps
          </button>
          {/*
            Driving directions FROM the venue TO the two destinations
            attendees travel to from SCUHS: hotel after sessions, Yard
            House for the Thursday group dinner. Mirrors the airport-
            origin buttons on the Lodging card.
          */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => openDirections(VENUE.address, HOTEL_QUERY)}
              style={{ ...mapBtn, flex: 1, ...mapBtnSecondary }}
            >
              <Ic.Map size={14} color={ACCENT} /> To Hotel
            </button>
            <button
              onClick={() => openDirections(VENUE.address, DINNER.address)}
              style={{ ...mapBtn, flex: 1, ...mapBtnSecondary }}
            >
              <Ic.Map size={14} color={ACCENT} /> To Yard House
            </button>
          </div>
          <button
            onClick={() => setShowDirections((v) => !v)}
            aria-expanded={showDirections}
            style={expanderBtn}
          >
            <span>Getting to campus</span>
            {showDirections ? (
              <Ic.ChevronUp size={14} color={ACCENT} />
            ) : (
              <Ic.ChevronDown size={14} color={ACCENT} />
            )}
          </button>
        </div>
        {showDirections && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 12,
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <ul
              style={{
                margin: '0 0 12px',
                paddingLeft: 18,
                fontSize: 13,
                color: TEXT_MID,
                lineHeight: 1.55,
              }}
            >
              <li>
                <b style={{ color: TEXT_DARK }}>Drive in</b> off Amber Valley Drive
                through Lot 4, then park in Lot 2 or Lot 3 (back of campus, near
                the M Building).
              </li>
              <li style={{ marginTop: 6 }}>
                <b style={{ color: TEXT_DARK }}>Thursday morning gathering:</b>{' '}
                F Building (front, near Lot 5) — informal meet-up before 8:00 AM.
              </li>
              <li style={{ marginTop: 6 }}>
                <b style={{ color: TEXT_DARK }}>Sessions both days:</b> Room M-71
                (M Building, back of campus).
              </li>
              <li style={{ marginTop: 6 }}>
                <b style={{ color: TEXT_DARK }}>Thursday lunch:</b> The Glen
                (outdoors, between the trees — marked red on the map).
              </li>
            </ul>
            <button
              onClick={() => setShowMap(true)}
              style={{ ...mapBtn, ...mapBtnSecondary }}
            >
              <Ic.Map size={14} color={ACCENT} /> View campus map
            </button>
          </div>
        )}
      </Card>

      <SectionLabel>LODGING</SectionLabel>
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>
          {HOTEL_NAME}
        </div>
        <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5, marginBottom: 12 }}>
          La Habra, CA
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => openPlace(HOTEL_QUERY)} style={mapBtn}>
            <Ic.Map size={15} color="#fff" /> Open in Maps
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => openDirections('LAX International Airport', HOTEL_QUERY)}
              style={{ ...mapBtn, flex: 1, ...mapBtnSecondary }}
            >
              <Ic.Map size={14} color={ACCENT} /> From LAX
            </button>
            <button
              onClick={() => openDirections('John Wayne Airport, Santa Ana', HOTEL_QUERY)}
              style={{ ...mapBtn, flex: 1, ...mapBtnSecondary }}
            >
              <Ic.Map size={14} color={ACCENT} /> From SNA
            </button>
          </div>
        </div>
      </Card>

      <SectionLabel>GROUP DINNER</SectionLabel>
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>
          {DINNER.name}
        </div>
        <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5 }}>
          {DINNER.address.split(',')[0]}
          <br />
          {DINNER.address.split(',').slice(1).join(',').trim()}
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: ACCENT,
            margin: '8px 0 12px',
          }}
        >
          {DINNER.when}
        </div>
        <button onClick={() => openPlace(DINNER.address)} style={mapBtn}>
          <Ic.Map size={15} color="#fff" /> Open in Maps
        </button>
      </Card>

      </ColumnWrap>
      <ColumnWrap>
      <SectionLabel>WI-FI</SectionLabel>
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {WIFI.map((row) => (
          <div
            key={row.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: TEXT_LIGHT,
                  letterSpacing: '0.06em',
                  marginBottom: 2,
                }}
              >
                {row.label}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: row.value ? TEXT_DARK : '#cbd5e1',
                  fontFamily: 'monospace',
                  letterSpacing: '0.04em',
                }}
              >
                {row.value || 'To be provided onsite'}
              </div>
            </div>
            {row.value ? (
              <button
                onClick={() => copy(row.key, row.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  background: copied === row.key ? '#dcfce7' : '#f8fafc',
                  border: `1px solid ${copied === row.key ? '#86efac' : '#e2e8f0'}`,
                  borderRadius: 7,
                  padding: '6px 11px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: copied === row.key ? '#16a34a' : '#64748b',
                  fontFamily: FONT,
                  transition: 'all 0.15s',
                }}
              >
                {copied === row.key ? (
                  <Ic.Check size={12} color="#16a34a" />
                ) : (
                  <Ic.Copy size={12} color="#64748b" />
                )}
                {copied === row.key ? 'Copied' : 'Copy'}
              </button>
            ) : null}
          </div>
        ))}
      </Card>

      <SectionLabel>KEY CONTACTS</SectionLabel>
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {CONTACTS.map((c, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: TEXT_LIGHT,
                  letterSpacing: '0.06em',
                  marginBottom: 2,
                  textTransform: 'uppercase',
                }}
              >
                {c.role}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>{c.name}</div>
              <div style={{ fontSize: 12.5, color: TEXT_MID, marginTop: 1 }}>
                {formatPhone(c.phone)}
              </div>
            </div>
            <a
              href={`tel:${c.phone}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: ACCENT_LIGHT,
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: 8,
                padding: '8px 13px',
                textDecoration: 'none',
                fontSize: 12.5,
                fontWeight: 600,
                color: ACCENT,
                fontFamily: FONT,
                flexShrink: 0,
              }}
            >
              <Ic.Phone size={14} color={ACCENT} /> Call
            </a>
          </div>
        ))}
      </Card>
      </ColumnWrap>
      {showMap && <MapLightbox onClose={() => setShowMap(false)} />}
    </OuterWrap>
  );
}

// Fullscreen map viewer. Click outside, hit Esc, or tap the X to dismiss.
// The image is centered, scaled to fit the viewport, and pinch-zoomable on
// mobile. A small "Download" link gives attendees a way to save the file.
function MapLightbox({ onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Prevent the body from scrolling underneath the overlay.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-label="SCUHS Campus Map"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10,16,32,0.85)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close map"
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 36,
          height: 36,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Ic.Close size={18} color="#fff" />
      </button>

      <img
        onClick={(e) => e.stopPropagation()}
        src={CAMPUS_MAP_URL}
        alt="SCUHS campus map"
        style={{
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 96px)',
          objectFit: 'contain',
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      />

      <a
        href={CAMPUS_MAP_URL}
        download="SCUHS_Campus_Map.png"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          bottom: 18,
          background: ACCENT,
          color: '#fff',
          textDecoration: 'none',
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: 600,
          padding: '9px 16px',
          borderRadius: 9,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        Download
      </a>
    </div>
  );
}

// Stable wrapper component identities — see comment in the main component.
function DesktopLogisticsWrap({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        alignItems: 'start',
      }}
    >
      {children}
    </div>
  );
}

function DesktopColumn({ children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>;
}

function PassThrough({ children }) {
  return <>{children}</>;
}

const mapBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  background: ACCENT,
  border: 'none',
  borderRadius: 8,
  padding: '9px 16px',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: FONT,
};

const mapBtnSecondary = {
  background: ACCENT_LIGHT,
  color: ACCENT,
  border: `1px solid ${ACCENT_BORDER}`,
};

// Toggle button that lives inside the Venue card and reveals the directions.
// Looks lighter than the primary "Open in Maps" CTA so the two don't fight.
const expanderBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  background: 'transparent',
  border: `1px dashed ${ACCENT_BORDER}`,
  borderRadius: 8,
  padding: '9px 16px',
  color: ACCENT,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: FONT,
};

function formatPhone(raw) {
  const d = raw.replace(/[^\d]/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}
